import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/services/hooks";
import {
  getPageTypeOptions,
  clearSubcategories,
} from "@/services/categoryServices/categoryAction";
import { getPosts, getBlog } from "@/services/blogServices/blogActions";
import PageTypesCreateView from "./PageTypesCreateView";
import type { PageTypesCreateViewProps } from "./PageTypesCreateView";
import {
  extractProductsFromSubcats,
  isProductPathFree,
  isSubcategoryPageFree,
  deriveAuthors,
} from "./pageTypesCreateHelpers";
import type { RootState } from "@/services/store";
import type { PageTypeSelection } from "@/services/pageServices/pagesSlice";
import "./PageType.css";
import "@/assets/css/FormValidation.css";

// Page sizes of the three pickers. These never changed at runtime (a useRef
// and two setter-less useState), so they live at module scope and stay out of
// the dependency arrays below.
const POSTS_PAGE_SIZE = 12;
const PRODUCTS_PER_PAGE = 8;
const SUBCATEGORIES_PER_PAGE = 4;

// Stable empty-posts fallback (SCRUM-898): sharing one array instead of a fresh
// `[]` per run keeps the blog-sync effect from thrashing state when
// the blog slice or its `posts` list is missing.
const EMPTY_POSTS: never[] = [];

/** What the wizard reports up for the picked page type (PageFormContainer). */
export type PageTypeConfiguration = { type: PageTypeName; object_target: unknown; path: string };

/** The page types this step offers: the slice's selection union, by type. */
type PageTypeName = NonNullable<PageTypeSelection["type"]>;

/** A picked entity: its `_id` is what `requiresChecker` keeps. */
type PickedEntity = { _id?: unknown };

export type PageTypesCreateContainerProps = {
  onErrorChange?: (hasError: boolean) => void;
  onConfigurationChange?: (configuration: PageTypeConfiguration) => void;
  isValidationAttempted?: boolean;
  derivedPath?: string;
  derivedPathNotice?: string;
};

export default function PageTypesCreateContainer(props: PageTypesCreateContainerProps) {
  const dispatch = useAppDispatch();

  //  Redux slices (read-only)
  const Currentsite = useAppSelector((s) => s.sites.site);
  // SCRUM-1809: the used-target filters read the site's pages from
  // `GET /api/pages/pagesBySite` (every page, projected) rather than from
  // the site document's `pages`, which it delivered 50 at a time — so
  // the picker used to offer a target whose page simply sat in a later batch.
  // The fetch lives in `PageFormContainer`, this component's wizard root
  // (PageFormContainer -> PageFormView -> here): one request per screen, and
  // this component stays renderable on its own without reaching the network.
  const sitePages = useAppSelector((s) => s.pages.pages) || [];
  const SubCategoriesFromRedux =
    useAppSelector((s) => s.categories.pageTypeSubcategories) || [];
  const blog = useAppSelector((s) => s.blog);

  //  Local state, grouped per domain. The grouping is declaration-order only:
  //  the effects below keep their original relative order.

  //  Blog/posts picker: post list, its search source, authors, pagination
  const [Posts, setPosts] = useState<any[]>([]); // any: SCRUM-2128 moves this state into a reducer
  const [PostsTosearchFrom, setPostsTosearchFrom] = useState<any[]>([]); // any: SCRUM-2128 moves this state into a reducer
  const [blogAvailability, setBlogAvailability] = useState(false);
  const [postsAvailability, setPostsAvailability] = useState(false);
  const [authors, setAuthors] = useState<any[]>([]); // any: SCRUM-2128 moves this state into a reducer
  const [currentPage, setCurrentPage] = useState(0);
  const [pagesCount, setPagesCount] = useState(0);

  //  Site catalog: category, subcategory and product pickers
  const [CurrentSite, setCurrentSite] = useState<RootState["sites"]["site"]>({});
  const [CategoryList, setCategoryList] = useState<any[]>([]); // any: SCRUM-2128 moves this state into a reducer
  const [currentPageProducts, setCurrentPageProducts] = useState(0);
  const [productsToSearchFrom, setProductsToSearchFrom] = useState<any[]>([]); // any: SCRUM-2128 moves this state into a reducer
  const [currentPageSubcats, setCurrentPageSubcats] = useState(0);
  const [subcategoriesToSearchFrom, setSubcategoriesToSearchFrom] = useState<any[]>([]); // any: SCRUM-2128 moves this state into a reducer
  // The typed search is state, and the displayed lists are DERIVED from it
  // below (SCRUM-1580). They used to be state set by the search handler and
  // reset to the unfiltered source by the redux-sync effect — so every
  // sub-category response that landed after the editor typed dropped the
  // filter and showed page 1 of the unfiltered list again. The fetches run one
  // per site category, so on a slow stack that is the common case.
  const [productQuery, setProductQuery] = useState("");
  const [subcategoryQuery, setSubcategoryQuery] = useState("");

  //  Page-type selection: chosen type, target object, path and radio value
  const [checker, setChecker] = useState({
    simple_page: false,
    category: false,
    subcategory: false,
    product: false,
    posts: false,
    authors: false,
  });
  // An entity id, a path, or null once a type is (re)selected: truthiness only.
  const [requiresChecker, setRequiresChecker] = useState<any>(""); // any: SCRUM-2128 moves this state into a reducer
  const [pathSimplePage, setPathSimplePage] = useState("");
  const [radioGroupValue, setRadioGroupValue] = useState("default");
  // SCRUM-1575: the routing-path field for a category / sub_category page.
  // `entityTarget` is the picked entity (the card handlers keep only its id in
  // requiresChecker); `pathEntity` is what the editor typed, and it counts as
  // typed only while non-blank — an emptied field falls back to the derived
  // value the parent computes, which arrives as props.derivedPath.
  const [entityTarget, setEntityTarget] = useState<{ type: PageTypeName; object: any } | null>(null);
  const [pathEntity, setPathEntity] = useState("");
  const pathEntityTyped = pathEntity.trim() !== "";

  // Helpers
  const isPathAlreadyExists = useCallback(
    (prod: { path?: string }) => isProductPathFree(sitePages, prod),
    [sitePages]
  );

  // SCRUM-1452: the sub-category picker offers targets for a NEW listing page, so
  // a sub-category that already has one does not belong in it. The product picker
  // has filtered this way since SCRUM-1032 (isPathAlreadyExists above); the
  // sub-category picker never did.
  const isSubcategoryFree = useCallback(
    (sub: Parameters<typeof isSubcategoryPageFree>[1]) => isSubcategoryPageFree(sitePages, sub),
    [sitePages]
  );

  const isPageTypeSelected = useCallback(() => {
    return Object.values(checker).some((v) => v === true);
  }, [checker]);

  // Thin wrapper: the derivation is deriveAuthors (pageTypesCreateHelpers,
  // SCRUM-1032); only the setters stay here. [] deps preserved (SCRUM-898: this
  // callback must not depend on `Posts`, or the blog-sync effect thrashes).
  const prepareAuthors = useCallback((blogData: Parameters<typeof deriveAuthors>[0]) => {
    const { authors, blogAvailability, postsAvailability } = deriveAuthors(blogData);
    setAuthors(authors);
    setBlogAvailability(blogAvailability);
    setPostsAvailability(postsAvailability);
  }, []);

  // Data fetching
  const loadInitialData = useCallback(async () => {
    await Promise.all([
      dispatch(getBlog()),
      dispatch(getPosts()),
      dispatch(clearSubcategories()),
    ]);
  }, [dispatch]);

  // Effects
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);


  useEffect(() => {
    if (Currentsite) {
      setCurrentSite(Currentsite);
      const cats = Currentsite.categories || [];
      setCategoryList(cats);
    }
  }, [Currentsite]);

  useEffect(() => {
    const posts = blog?.posts || EMPTY_POSTS;
    setPosts(posts);
    setPostsTosearchFrom(posts);
    setPagesCount(Math.ceil(posts.length / POSTS_PAGE_SIZE));
    prepareAuthors(blog);
  }, [blog, prepareAuthors]);

  // One request for the whole picker (SCRUM-1653 slice 2): GET /api/pages/type-options
  // answers the site's categories → subcategories → products in one response,
  // replacing the per-category fetch loop (1 + N requests, N = the site's
  // category count) and the list it appended into.
  useEffect(() => {
    if (Currentsite?._id) dispatch(getPageTypeOptions());
  }, [Currentsite?._id, dispatch]);

  useEffect(() => {
    const { subcategories, products } = extractProductsFromSubcats(
      SubCategoriesFromRedux,
      CurrentSite
    );

    setSubcategoriesToSearchFrom(subcategories);
    setProductsToSearchFrom(products);
  }, [SubCategoriesFromRedux, CurrentSite]);

  // Validation sync with parent. Listed by name, not as `props`: `props` is a new
  // object on every parent render, so the effect re-reported unchanged validity
  // each time the wizard re-rendered (SCRUM-2193).
  const { onErrorChange } = props;
  useEffect(() => {
    const ok =
      isPageTypeSelected() &&
      (checker.simple_page ||
        (checker.category && !!requiresChecker) ||
        (checker.subcategory && !!requiresChecker) ||
        (checker.product && !!requiresChecker) ||
        (checker.posts && !!requiresChecker) ||
        (checker.authors && !!requiresChecker));

    onErrorChange?.(!ok);
  }, [checker, requiresChecker, isPageTypeSelected, onErrorChange]);

  // One writer for the type-selection emission: the report to the parent, the
  // only route the pick takes (SCRUM-2206; it went through the store as well
  // until then). The 8 interactive handlers below all go through it
  // (SCRUM-1032). object === object_target for every legacy site; path is ""
  // except for the simple_page cases where it mirrors the object. The parent's
  // handler guards the pair (pageTypePayloadDefect, SCRUM-1650). The update
  // screen has its own prefill in PageTypesUpdateContainer; this component is
  // the create wizard's only.
  const { onConfigurationChange } = props;
  const reportTypeSelection = useCallback(
    (type: PageTypeName, object: unknown, path: string | undefined = "") => {
      onConfigurationChange?.({ type, object_target: object, path });
    },
    [onConfigurationChange]
  );

  // Handlers
  const onChangeRadioInputHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = "";
      switch (e.currentTarget.value) {
        case "error":
          value = "404";
          break;
        case "maintenance":
          value = "maintenance";
          break;
        case "redirect":
          value = "redirect";
          break;
        default:
          break;
      }
      // The radio values are "error" / "maintenance" / "redirect"; reported as before.
      reportTypeSelection(e.currentTarget.value as PageTypeName, value);
      setRadioGroupValue(e.currentTarget.value);
      setPathSimplePage(value);
    },
    [reportTypeSelection]
  );

  const onChangeSimplePageInputHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      reportTypeSelection("simple_page", e.target.value, e.target.value);
      setPathSimplePage(e.target.value);
    },
    [reportTypeSelection]
  );

  const onClickproductsHandler = useCallback(
    (productWrapper: { product: PickedEntity }) => {
      reportTypeSelection("product", productWrapper);
      setRequiresChecker(productWrapper.product._id);
    },
    [reportTypeSelection]
  );

  // SCRUM-1575: a typed path survives re-picking (select A, type, select B keeps
  // the typed value); an untouched field reports "" and follows B's derived path.
  const onClickCategoryHandler = useCallback(
    (category: PickedEntity) => {
      reportTypeSelection("category", category, pathEntityTyped ? pathEntity : "");
      setRequiresChecker(category._id);
      setEntityTarget({ type: "category", object: category });
    },
    [reportTypeSelection, pathEntity, pathEntityTyped]
  );

  const onClickSubCategoryHandler = useCallback(
    (sub_category: PickedEntity) => {
      reportTypeSelection("sub_category", sub_category, pathEntityTyped ? pathEntity : "");
      setRequiresChecker(sub_category._id);
      setEntityTarget({ type: "sub_category", object: sub_category });
    },
    [reportTypeSelection, pathEntity, pathEntityTyped]
  );

  const onChangeEntityPathHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setPathEntity(value);
      if (entityTarget) {
        reportTypeSelection(entityTarget.type, entityTarget.object, value.trim() ? value : "");
      }
    },
    [entityTarget, reportTypeSelection]
  );

  const onClickPostHandler = useCallback(
    (post: PickedEntity) => {
      reportTypeSelection("post", post);
      setRequiresChecker(post._id);
    },
    [reportTypeSelection]
  );

  const onClickAuthorHandler = useCallback(
    (author: PickedEntity) => {
      reportTypeSelection("author", author);
      setRequiresChecker(author._id);
    },
    [reportTypeSelection]
  );

  const selectType = useCallback(
    (name: keyof typeof checker) => {
      setRequiresChecker(null);
      setEntityTarget(null);
      setPathEntity("");
      setChecker({
        simple_page: false,
        category: false,
        subcategory: false,
        product: false,
        posts: false,
        authors: false,
        [name]: true,
      });
      if (name === "simple_page") {
        reportTypeSelection("simple_page", pathSimplePage, pathSimplePage);
      }
    },
    [pathSimplePage, reportTypeSelection]
  );

  // Search handlers
  const searchPost = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const q = e.target.value;
      if (q?.length) {
        const SearchPosts = (PostsTosearchFrom || []).filter((post) =>
          post.title?.toLowerCase().includes(q.toLowerCase())
        );
        setPosts(SearchPosts);
        setPagesCount(Math.ceil(SearchPosts.length / POSTS_PAGE_SIZE));
        setCurrentPage(0);
      } else {
        setPosts(PostsTosearchFrom || []);
        setPagesCount(
          Math.ceil((PostsTosearchFrom || []).length / POSTS_PAGE_SIZE)
        );
        setCurrentPage(0);
      }
    },
    [PostsTosearchFrom]
  );

  const searchProduct = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setProductQuery(e.target.value?.toLowerCase() || "");
    setCurrentPageProducts(0);
  }, []);

  const searchSubcategory = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSubcategoryQuery(e.target.value?.toLowerCase() || "");
    setCurrentPageSubcats(0);
  }, []);

  // Re-applied whenever the source list changes, so a fetch that completes
  // after the editor typed narrows to the query instead of replacing it.
  const ProductsList = useMemo(() => {
    if (!productQuery.length) return productsToSearchFrom;
    return (productsToSearchFrom || []).filter((p) =>
      p?.product?.title?.toLowerCase().includes(productQuery)
    );
  }, [productsToSearchFrom, productQuery]);

  const SubCategoriesList = useMemo(() => {
    if (!subcategoryQuery.length) return subcategoriesToSearchFrom;
    return (subcategoriesToSearchFrom || []).filter((sc) =>
      sc?.name?.toLowerCase().includes(subcategoryQuery)
    );
  }, [subcategoriesToSearchFrom, subcategoryQuery]);

  // Pagination handlers
  const handlePostsPageClick = useCallback((e: { preventDefault?: () => void } | undefined, index: number) => {
    e?.preventDefault?.();
    setCurrentPage(index);
  }, []);

  const handleProductsPaginationClick = useCallback((pageIndex: number) => {
    setCurrentPageProducts(pageIndex);
  }, []);

  const handleSubcategoriesPaginationClick = useCallback((pageIndex: number) => {
    setCurrentPageSubcats(pageIndex);
  }, []);

  // Derived data for view
  const filteredProductsSorted = useMemo(() => {
    return (ProductsList || [])
      .filter((p) => p.product !== null)
      .filter((p) => (p.product?.linked?.length || 0) > 0)
      .filter(isPathAlreadyExists)
      .sort((a, b) => b.product._id.localeCompare(a.product._id));
  }, [ProductsList, isPathAlreadyExists]);

  const totalProductPages = useMemo(
    () => Math.ceil(filteredProductsSorted.length / PRODUCTS_PER_PAGE),
    [filteredProductsSorted.length]
  );
  const displayedProducts = useMemo(() => {
    const start = currentPageProducts * PRODUCTS_PER_PAGE;
    return filteredProductsSorted.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredProductsSorted, currentPageProducts]);

  const sortedSubcategories = useMemo(() => {
    return [...(SubCategoriesList || [])]
      .filter(isSubcategoryFree)
      .sort(
        (a, b) =>
          b.products.filter((pr) => pr.product !== null).length -
          a.products.filter((pr) => pr.product !== null).length
      );
  }, [SubCategoriesList, isSubcategoryFree]);

  const totalSubcategoryPages = useMemo(
    () => Math.ceil(sortedSubcategories.length / SUBCATEGORIES_PER_PAGE),
    [sortedSubcategories.length]
  );

  const displayedSubcategories = useMemo(() => {
    const start = currentPageSubcats * SUBCATEGORIES_PER_PAGE;
    return sortedSubcategories.slice(start, start + SUBCATEGORIES_PER_PAGE);
  }, [sortedSubcategories, currentPageSubcats]);

  const slicedPosts = useMemo(() => {
    const start = currentPage * POSTS_PAGE_SIZE;
    return (Posts || [])
      .slice(start, start + POSTS_PAGE_SIZE)
      .filter((p) => !p.active);
  }, [Posts, currentPage]);

  const viewProps: PageTypesCreateViewProps = {
    checker,
    requiresChecker,
    pathSimplePage,
    pathEntity: pathEntityTyped ? pathEntity : props.derivedPath ?? "",
    // SCRUM-1704: only while the field shows the derived value — a typed path
    // is the editor's, and the notice is about where the derived one came from.
    derivedPathNotice: pathEntityTyped ? "" : props.derivedPathNotice ?? "",
    radioGroupValue,
    blogAvailability,
    postsAvailability,
    authors,
    CurrentSite,
    CategoryList,
    SubCategoriesList,
    ProductsList,

    displayedProducts,
    totalProductPages,
    currentPageProducts,
    displayedSubcategories,
    totalSubcategoryPages,
    currentPageSubcats,

    slicedPosts,
    currentPage,
    pagesCount,

    isValidationAttempted: props.isValidationAttempted,
    isPageTypeSelected: isPageTypeSelected(),

    selectType,
    onChangeSimplePageInputHandler,
    onChangeEntityPathHandler,
    onChangeRadioInputHandler,
    onClickCategoryHandler,
    onClickSubCategoryHandler,
    onClickproductsHandler,
    onClickPostHandler,
    onClickAuthorHandler,

    searchPost,
    searchProduct,
    searchSubcategory,

    handlePostsPageClick,
    handleProductsPaginationClick,
    handleSubcategoriesPaginationClick,
  };

  return <PageTypesCreateView {...viewProps} />;
}
